import { useState, type FormEvent } from 'react';
import type { Contact } from '@neuroedge/shared';
import { Button, Modal, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useCreateContact } from '../../api/contacts';
import { EMPTY_CONTACT_FORM, validateContactForm, type ContactFormShape } from './ContactFormPage';

const trimOrUndef = (v: string) => v.trim() || undefined;

/**
 * Creates a contact without leaving the current form (e.g. a new lead for
 * someone who isn't a contact yet) - just the essentials; the rest can be
 * filled in on the contact page later.
 */
export function QuickContactModal({
  onCreated,
  onClose,
}: {
  onCreated: (contact: Contact) => void;
  onClose: () => void;
}) {
  const create = useCreateContact();
  const toast = useToast();
  const [form, setForm] = useState<ContactFormShape>(EMPTY_CONTACT_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const set = (key: keyof ContactFormShape) => (e: { target: { value: string } }) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setFieldErrors((f) => {
      if (!f[key]) return f;
      const { [key]: _removed, ...rest } = f;
      return rest;
    });
  };

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const errors = validateContactForm(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      return;
    }
    try {
      const contact = await create.mutateAsync({
        displayName: form.displayName.trim(),
        kind: 'PERSON',
        companyName: trimOrUndef(form.companyName),
        email: trimOrUndef(form.email),
        phone: trimOrUndef(form.phone),
        status: 'ACTIVE',
      });
      toast.success(`Contact "${contact.displayName}" created.`);
      onCreated(contact);
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
          return;
        }
      }
      setError(
        err instanceof ApiClientError && err.httpStatus < 500
          ? err.message
          : 'Could not create the contact.',
      );
    }
  }

  return (
    <Modal title="New contact" onClose={onClose}>
      <form className="ne-auth__form" onSubmit={(e) => void onSubmit(e)} noValidate>
        <TextField
          label="Name"
          required
          autoFocus
          value={form.displayName}
          onChange={set('displayName')}
          error={fieldErrors.displayName}
        />
        <TextField
          label="Phone"
          inputMode="tel"
          placeholder="e.g. +91 98765 43210"
          value={form.phone}
          onChange={set('phone')}
          error={fieldErrors.phone}
        />
        <TextField
          label="Email"
          type="email"
          value={form.email}
          onChange={set('email')}
          error={fieldErrors.email}
        />
        <TextField
          label="Company"
          value={form.companyName}
          onChange={set('companyName')}
          error={fieldErrors.companyName}
        />
        {error ? (
          <p className="ne-auth__error" role="alert">
            {error}
          </p>
        ) : null}
        <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create contact'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
